'use client';

import React, { useState } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import axios from 'axios';
import { Container } from '../layouts';
import styles from './styles.module.scss';
import { Button } from '../Button';
import { constructClassName } from '../../utils/constructClassName';
import { AddressForm, LeadDetailsForm, ProgressBar, RenderFields } from './components';
import type { GetSourceFromApiKeyQuery } from '../../lib/ecoButlerStat/generated';
import { Logo } from '../graphics';

interface Props {
  leadFormId: string;
  questions: GetSourceFromApiKeyQuery['getSourceFromApiKey']['questions'];
  isEmbedded?: boolean;
}

export const DynamicForm: React.FC<Props> = ({
  isEmbedded = false,
  questions,
  leadFormId,
}) => {
  const [loading, setLoading] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  const { control, handleSubmit, trigger, setValue } = useForm();
  const router = useRouter();

  const onSubmit = async (data: FieldValues) => {
    setLoading(true);

    let { houseNameNumber, phone, ...restData } = data;

    if (!phone.startsWith('+')) {
      phone = `+44${phone.replace(/^0/, '')}`;
      data.phone = phone;
    }

    const submittedLead = await axios.post(`${process.env.NEXT_PUBLIC_DEV_CLIENT_URL || process.env.NEXT_PUBLIC_CLIENT_URL}/api/lead-form/submit-lead`, {
      leadFormId,
      phone,
      ...restData,
    });

    if (typeof submittedLead.data === 'string') {
      router.push(`/results?code=${submittedLead.data}`);
    } else {
      setLoading(false);
      // eslint-disable-next-line no-alert
      alert('Error submitting form');
    }
  };

  const handlePrevious = () => {
    setCurrentQuestionIndex(currentQuestionIndex - 1);
  };

  const handleNext = async () => {
    const formValidated = await trigger();
    if (!formValidated) return;
    setCurrentQuestionIndex(currentQuestionIndex + 1);
  };

  return (
    <form
      className={styles.form}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Container className={constructClassName([
        styles.container,
        isEmbedded && styles.embeddedContainer,
      ])}
      >
        {
          !isEmbedded && (
            <React.Fragment>
              <ProgressBar
                totalSections={questions?.length || 0}
                currentSection={currentQuestionIndex}
              />
              <Logo
                width={200}
                className={styles.logo}
              />
            </React.Fragment>
          )
        }
        <React.Fragment>
          {questions?.map((field, index) => (
            <div
              key={index}
              className={constructClassName([
                styles.questionSection,
                currentQuestionIndex === index && styles.questionSectionActive,
              ])}
            >
              <RenderFields
                type={field.type}
                control={control}
                currentQuestionIndex={currentQuestionIndex}
                handleNext={handleNext}
                question={{
                  index,
                  question: field.question,
                  type: field.type,
                  options: field.options || [],
                }}
              />
            </div>
          ))}
          {
            currentQuestionIndex < (questions?.length || 0) && (
              <div className={styles.buttonsContainer}>
                <Button
                  title="Back"
                  disabled={currentQuestionIndex === 0}
                  onClick={handlePrevious}
                  type='secondary'
                >
                  Back
                </Button>
                <Button
                  title="Next"
                  disabled={currentQuestionIndex === (questions?.length || 0)}
                  onClick={handleNext}
                >
                  Next
                </Button>
              </div>
            )
          }
        </React.Fragment>
        {
          currentQuestionIndex === (questions?.length || 0) && (
            <AddressForm
              control={control}
              handleNext={handleNext}
              leadFormId={leadFormId}
              setValue={setValue}
              handlePrevious={handlePrevious}
            />
          )
        }
        {
          currentQuestionIndex === (questions?.length || 0) + 1 && (
            <LeadDetailsForm
              control={control}
              handlePrevious={handlePrevious}
              loading={loading}
            />
          )
        }
        {
          !isEmbedded && (
            <React.Fragment>
              {' '}
              {/* <div className={styles.penguinVectorContainer}>
                <img
                  alt="Illustration of a penguin holding a platter"
                  src="/images/ButlerPenguin-01.png"
                  className={styles.penguinVector}
                />
              </div> */}
              <div className={styles.footerLinksContainer}>
                <Link
                  href="/privacy-policy"
                  passHref
                  target="_blank"
                >
                  Privacy Policy
                </Link>
                <Link
                  href="/cookie-policy"
                  passHref
                  target="_blank"
                >
                  Cookie Policy
                </Link>
              </div>
            </React.Fragment>

          )
        }
      </Container>
    </form>
  );
};
